import AddIcon from '@mui/icons-material/Add'
import ComputerIcon from '@mui/icons-material/Computer'
import FolderIcon from '@mui/icons-material/Folder'
import { Box, Fab, Menu, MenuItem, Typography, useTheme } from '@mui/material'
import type { ChangeEvent } from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { toast } from 'react-toastify'

import { useAppSelector } from '@/hooks/redux'
import { useLazyGetImagePresignedUrlQuery } from '@/store/apiService'
import { setNewImages } from '@/store/appSlice'
import { getSupportedImagetypes } from '@/utils/constants'

import type { PresignedUrlResponse } from '../Gallery'
import ProjectImageList from './ProjectImageList'

interface GalleryUploaderProps {
  disabled?: boolean
}

const GalleryUploader: React.FC<GalleryUploaderProps> = ({ disabled }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'gallery' })

  const theme = useTheme()
  const location = useLocation()
  const dispatch = useDispatch()

  const newImages = useAppSelector((state) => state.app.newImages || [])
  const currentCaseId = useAppSelector((state) => state.session.currentCaseId)

  const [menuAnchorElement, setMenuAnchorElement] =
    useState<null | HTMLElement>(null)
  const [imageList, setImageList] = useState(false)
  const isMenuOpen = Boolean(menuAnchorElement)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [getImagePresignedUrl] = useLazyGetImagePresignedUrlQuery()

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
    setMenuAnchorElement(event.currentTarget)

  const handleMenuClose = () => setMenuAnchorElement(null)

  const handleImageUpload = async (files: File[]) => {
    try {
      const uploads = await Promise.all(
        files.map(async (file) => {
          const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, ' ')
          const presignedData = (await getImagePresignedUrl({
            caseId: currentCaseId || '',
            fileName: sanitizedFileName,
          }).unwrap()) as PresignedUrlResponse

          return { presignedData, file }
        }),
      )

      toast.info('Uploading started.')

      const updatedImages = [
        ...uploads,
        ...(Array.isArray(newImages) ? newImages : []),
      ]
      dispatch(setNewImages(updatedImages))
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Upload failed: ${error.message}`)
      } else {
        toast.error('An unexpected error occurred during upload')
      }
    }
  }

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const validFiles: File[] = []
      Array.from(event.target.files).forEach((file) => {
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, ' ')
        const fileExtension = sanitizedFileName.split('.').pop()?.toLowerCase()

        if (
          !fileExtension ||
          !getSupportedImagetypes(location.pathname).includes(
            fileExtension as any,
          )
        ) {
          toast.error(`Invalid file type: ${file.name}`)
          return
        }
        validFiles.push(file)
      })

      if (validFiles.length > 0) {
        await handleImageUpload(validFiles)
      }
    }
    handleMenuClose()
  }

  const handleUploadFromComputer = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleUploadFromProject = () => {
    setImageList(true)
  }

  return (
    <Box position="absolute" bottom={10} left="43%">
      <Fab
        size="small"
        color="inherit"
        aria-label="add image"
        sx={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.text.primary}`,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            transform: 'scale(1.1)',
          },
        }}
        onClick={handleMenuOpen}
        aria-controls={isMenuOpen ? 'upload-menu' : undefined}
        aria-expanded={isMenuOpen ? 'true' : undefined}
        disabled={disabled}
      >
        <AddIcon />
      </Fab>
      <Menu
        id="upload-menu"
        anchorEl={menuAnchorElement}
        open={isMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'upload-button',
        }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              '& .MuiMenuItem-root': {
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleUploadFromComputer}>
          <input
            type="file"
            onChange={handleFileUpload}
            multiple
            hidden
            // accept="image/*"
            ref={fileInputRef}
          />
          <ComputerIcon />
          <Typography pl={1}>{t('uploadFromComputer')}</Typography>
        </MenuItem>
        <MenuItem onClick={handleUploadFromProject}>
          <FolderIcon />
          <Typography pl={1}>{t('fromProject')}</Typography>
        </MenuItem>
      </Menu>
      {imageList && (
        <ProjectImageList
          open={imageList}
          onClose={() => setImageList(false)}
          setMenuAnchorElement={setMenuAnchorElement}
        />
      )}
    </Box>
  )
}

export default GalleryUploader
